// ── Landing module brief ────────────────────────────────────────────────────────────────────────
// The interstitial a landing module card opens on a plain desktop click: the module's identity,
// what it does, its top highlight points, who it is for, and the availability qualifier the
// catalogue carries — then two real exits (the full /modules/:key page, and the governed demo CTA).
//
// HONESTY: every string rendered here is READ from the canonical registries (moduleCatalog's
// `oneLiner` / `detail.what` / `detail.bullets` / `roles` / `publicStatus` / `kind`, and
// moduleDeepDiveContent's chapter title). Nothing is re-worded into a claim and nothing is
// invented — a module missing a field simply drops that section.
//
// A11Y: this is the shared ui/Modal primitive, so the whole modal contract comes from ONE place —
// role="dialog" + aria-modal, labelled by the module name, Tab trapped inside, Esc closes, focus
// restores to the invoking card, background scroll locked (+ #root inert) and `animate-modal-in`
// carrying `motion-reduce:animate-none`. `layerClassName="landing-page"` re-scopes the marketing
// type + theme tokens inside the body portal, exactly as ContactSalesModal does.
//
// MOBILE (deliberate, see LandingModuleExplorer's `useBriefViewport`): below 640px the card
// navigates straight to /modules/:key instead of opening this dialog. A phone-width dialog is a
// full-screen takeover with no URL — not shareable, not bookmarkable, and the browser Back button
// does not close it — which is strictly worse than the real page it would be summarising. So the
// brief is a desktop/tablet affordance and the phone gets the destination itself.
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, BookOpen, Check, Info, Users } from "lucide-react";
import { Modal } from "../../components/ui/Modal";
import ButtonLink from "../../components/marketing/ButtonLink";
import Module3DIcon from "../../components/marketing/Module3DIcon";
import { feature3dColor, module3dAsset } from "../../components/marketing/module3dAssets";
import { isPlainBookDemoActivation, requestBookDemo } from "../../components/marketing/shared";
import { CTA_SECONDARY_LABEL, DEMO_BOOKING_ROUTE } from "../../components/marketing/navigation";
import { BrandCopy } from "../../components/BrandWordmarkText";
import { track } from "../../lib/analytics";
import { deepDiveForModule } from "./moduleDeepDiveContent";

// The brief is a summary, not a second catalogue page — it shows the leading highlights and says
// honestly how many more the full page carries. (Every reviewed entry currently has four.)
export const BRIEF_HIGHLIGHT_LIMIT = 5;

export const moduleBriefPath = (mod) => `/modules/${mod?.key}`;

function SectionHeading({ icon: Icon, children }) {
  return (
    <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.11em] text-ink-subtle">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {children}
    </h4>
  );
}

export default function ModuleBriefDialog({ mod, open, accent, placement = "landing", onClose }) {
  const location = useLocation();
  const isOpen = Boolean(open && mod);
  // Hooks must run on every render, so read the module defensively rather than returning early.
  const name = mod?.name || "";
  const featureColor = feature3dColor(name, accent);
  const bullets = (mod?.detail?.bullets || []).filter(Boolean);
  const highlights = bullets.slice(0, BRIEF_HIGHLIGHT_LIMIT);
  const remaining = bullets.length - highlights.length;
  const roles = (mod?.roles || []).filter(Boolean);
  const deepDive = mod ? deepDiveForModule(mod) : null;
  const fullPath = mod ? moduleBriefPath(mod) : "/modules";

  const onFullDetails = () => {
    track("module_brief_full_details", { module: mod.key, workspace: mod.workspace, placement });
    onClose?.();
  };

  // The governed demo CTA. The shared Modal locks background scroll and marks #root inert while it
  // is open, so the landing page's own handler (which scrolls to the enquiry section) must never be
  // issued against a still-frozen document. The intent is therefore PARKED and released by the
  // effect below, which React runs only after the closed Modal's cleanup has restored the page —
  // deterministic, unlike counting animation frames.
  const [pendingDemo, setPendingDemo] = useState(null);
  useEffect(() => {
    if (!pendingDemo || isOpen) return;
    setPendingDemo(null);
    requestBookDemo({ contextLabel: pendingDemo, placement: "module_brief", analyticsTracked: true });
  }, [pendingDemo, isOpen]);

  const onBookDemo = (event) => {
    if (!isPlainBookDemoActivation(event)) return; // ⌘/ctrl/shift/middle-click keeps the real href
    event.preventDefault();
    track("demo_request", { placement: "module_brief", context: name, page: location.pathname });
    setPendingDemo(name);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={name}
      size="lg"
      closeLabel="Close module brief"
      layerClassName="landing-page"
      footer={(
        <>
          <ButtonLink
            to={DEMO_BOOKING_ROUTE}
            variant="outline"
            onClick={onBookDemo}
            data-testid="module-brief-demo"
            className="!rounded-full justify-center"
          >
            {CTA_SECONDARY_LABEL}
          </ButtonLink>
          <ButtonLink
            to={fullPath}
            onClick={onFullDetails}
            data-testid="module-brief-full-details"
            className="!rounded-full justify-center"
          >
            View full details <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </ButtonLink>
        </>
      )}
    >
      {/* The Aira launcher is a fixed z-50 element; the modal layer is z-[70] and #root is inert,
          so it can neither cover nor receive a click here. Marking the brief as a compact-width
          clearance surface (the mechanism ModuleBuyerGuidePage/BuyerGuideVisualStage already use)
          additionally hides the launcher outright while the brief owns a narrow viewport.
          `tabIndex=0` + a name are the WCAG 2.1.1 fix for the dialog body's scroll region: the
          brief is prose, so without a focusable descendant a keyboard user could not scroll it
          (axe `scrollable-region-focusable`, serious — the same idiom the HR summary track uses). */}
      <div
        data-testid="module-brief"
        data-module={mod.key}
        data-aira-fab-clearance="hide-compact"
        tabIndex={0}
        role="group"
        aria-label={`${name} — capability brief`}
        className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <div className="flex items-start gap-4">
          <Module3DIcon
            Icon={mod.Icon}
            src={module3dAsset(mod)}
            color={featureColor}
            size="md"
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-2"
                style={{ background: `color-mix(in srgb, ${featureColor} 14%, transparent)` }}>
                {mod.kind}
              </span>
              {mod.workspaceLabel && (
                <span className="text-[11.5px] font-semibold text-ink-subtle">{mod.workspaceLabel}</span>
              )}
            </p>
            <p className="mt-2 text-[14px] leading-[1.7] text-ink-2"><BrandCopy>{mod.oneLiner}</BrandCopy></p>
          </div>
        </div>

        {mod.detail?.what && (
          <section className="mt-6">
            <SectionHeading icon={Info}>What it does</SectionHeading>
            <p className="mt-2 text-[13.5px] leading-[1.75] text-ink-2"><BrandCopy>{mod.detail.what}</BrandCopy></p>
          </section>
        )}

        {highlights.length > 0 && (
          <section className="mt-6">
            <SectionHeading icon={Check}>Top highlights</SectionHeading>
            <ul className="mt-2.5 space-y-2">
              {highlights.map((point) => (
                <li key={point} className="flex gap-2.5 text-[13.5px] leading-[1.7] text-ink-2">
                  <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-positive" aria-hidden="true" />
                  <span><BrandCopy>{point}</BrandCopy></span>
                </li>
              ))}
            </ul>
            {remaining > 0 && (
              <p className="mt-2.5 text-[12.5px] leading-5 text-ink-subtle" data-testid="module-brief-more">
                {remaining} more {remaining === 1 ? "capability is" : "capabilities are"} listed on the full page.
              </p>
            )}
          </section>
        )}

        {roles.length > 0 && (
          <section className="mt-6">
            <SectionHeading icon={Users}>Who it&rsquo;s for</SectionHeading>
            <ul className="mt-2.5 flex flex-wrap gap-2">
              {roles.map((role) => (
                <li key={role} className="rounded-full border border-line bg-muted px-2.5 py-1 text-[12px] font-semibold text-ink-2">{role}</li>
              ))}
            </ul>
          </section>
        )}

        {(mod.publicStatus || deepDive) && (
          <section className="mt-6 rounded-2xl border border-line bg-muted/50 p-4">
            {mod.publicStatus && (
              <p className="text-[12.5px] leading-5 text-ink-2" data-testid="module-brief-availability">
                <span className="font-bold text-ink">Availability</span>
                <span aria-hidden="true"> · </span>
                {mod.publicStatus}
              </p>
            )}
            {deepDive && (
              <p className={`flex gap-2 text-[12.5px] leading-5 text-ink-2 ${mod.publicStatus ? "mt-2" : ""}`} data-testid="module-brief-deep-dive">
                <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
                <span>The full page carries a detailed chapter: <span className="font-semibold text-ink">{deepDive.title}</span>.</span>
              </p>
            )}
          </section>
        )}
      </div>
    </Modal>
  );
}
